import { appConfig, getApiBaseCandidates, isAbsoluteHttpUrl, resolveBackendAssetUrl } from '../../config/appConfig';

export type AdminContentKind = 'news' | 'resource' | 'career' | 'project-cooperation';
export type AdminConsoleModule = AdminContentKind | 'analytics' | 'account';

export type AdminPermissions = {
  can_manage_news: boolean;
  can_manage_resources: boolean;
  can_manage_careers: boolean;
  can_manage_project_cooperation: boolean;
  can_manage_analytics: boolean;
  can_manage_users: boolean;
};

export type AdminUser = {
  id: number;
  username: string;
  display_name: string;
  is_active: boolean;
  is_superuser: boolean;
  permissions: AdminPermissions;
};

export type AdminSummary = {
  news_group_count: number;
  resource_group_count: number;
  career_group_count: number;
  project_cooperation_group_count: number;
  published_news_group_count: number;
  published_resource_group_count: number;
  published_career_group_count: number;
  published_project_cooperation_group_count: number;
  news_version_count: number;
  resource_version_count: number;
  career_version_count: number;
  project_cooperation_version_count: number;
  console_user_count: number;
};

export type AdminAnalyticsOverview = {
  total_visits: number;
  unique_visitors: number;
  today_visits: number;
  today_unique_visitors: number;
  current_month_visits: number;
  current_year_visits: number;
};

export type AdminAnalyticsSeriesPoint = {
  label: string;
  visits: number;
  unique_visitors: number;
};

export type AdminAnalyticsPageRow = {
  path: string;
  page_title: string;
  visits: number;
  unique_visitors: number;
};

export type AdminAnalyticsRegionRow = {
  country: string;
  region: string;
  city: string;
  label: string;
  visits: number;
  unique_visitors: number;
};

export type AdminAnalyticsPayload = {
  overview: AdminAnalyticsOverview;
  daily_series: AdminAnalyticsSeriesPoint[];
  monthly_series: AdminAnalyticsSeriesPoint[];
  top_pages: AdminAnalyticsPageRow[];
  top_regions: AdminAnalyticsRegionRow[];
  latest_visit_at: string | null;
};

export type AdminPagination = {
  page: number;
  page_size: number;
  total_items: number;
  total_pages: number;
};

export type AdminCareerRequirement = {
  index: string;
  title: string;
  description: string;
};

export type AdminCooperationItem = {
  index: string;
  title: string;
  description: string;
};

export type AdminContentLocale = 'zh' | 'en' | 'ja';

export type AdminContentItem = {
  id: number;
  kind: AdminContentKind;
  group_key: string;
  language: AdminContentLocale;
  available_languages: AdminContentLocale[];
  slug: string;
  badge: string;
  title: string;
  summary: string;
  body: string;
  news_topics: string[];
  resource_category?: 'talent' | 'experts' | 'service-cases';
  requirements?: AdminCareerRequirement[];
  cooperation_items?: AdminCooperationItem[];
  questionnaire_title?: string;
  questionnaire_description?: string;
  contact_phone?: string;
  contact_email?: string;
  qr_hint?: string;
  qr_image_url?: string | null;
  published_at: string;
  is_published: boolean;
  is_pinned: boolean;
  is_default_display?: boolean;
  service_page_slot?: string;
  sort_order: number;
  cover_image_url?: string | null;
  created_at: string;
  updated_at: string;
};

export type AdminListResponse<T> = {
  ok: true;
  results: T[];
  pagination: AdminPagination;
  filters?: Record<string, string>;
};

export type AdminUserPayload = {
  username: string;
  display_name: string;
  is_active: boolean;
  is_superuser: boolean;
  can_manage_news: boolean;
  can_manage_resources: boolean;
  can_manage_careers: boolean;
  can_manage_project_cooperation: boolean;
  can_manage_analytics: boolean;
  can_manage_users: boolean;
};

export type CreateAdminUserPayload = AdminUserPayload & {
  password: string;
};

export type UpdateOwnAccountPayload = {
  username: string;
  display_name: string;
};

export type ChangeOwnPasswordPayload = {
  current_password: string;
  new_password: string;
};

const API_BASE_URL = appConfig.api.baseUrl;
export const CONTENT_CONSOLE_TOKEN_KEY = 'future_world_content_console_token';

export class ConsoleApiError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'ConsoleApiError';
    this.status = status;
  }
}

const buildHeaders = (token?: string, extraHeaders?: HeadersInit) => {
  const headers = new Headers(extraHeaders);
  if (token) headers.set('Authorization', `Bearer ${token}`);
  return headers;
};

const parseResponse = async <T>(response: Response): Promise<T> => {
  const contentType = response.headers.get('content-type') ?? '';
  const text = await response.text();

  if (contentType.includes('text/html') || text.trim().startsWith('<!DOCTYPE') || text.trim().startsWith('<html')) {
    throw new ConsoleApiError('接口返回了 HTML 页面，请检查 Django 后端是否启动、接口地址是否正确，以及 VITE_API_BASE_URL 是否指向 /api。', response.status || 500);
  }

  let data: unknown = {};
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      throw new ConsoleApiError('接口返回内容不是合法 JSON，请检查后端接口。', response.status || 500);
    }
  }

  if (!response.ok) {
    const message = typeof data === 'object' && data && 'error' in data ? String((data as { error: string }).error) : '请求失败';
    throw new ConsoleApiError(message, response.status);
  }

  return data as T;
};

const buildApiBases = () => {
  const bases = new Set<string>(getApiBaseCandidates());

  if (isAbsoluteHttpUrl(API_BASE_URL)) {
    bases.add(API_BASE_URL);
  }

  return Array.from(bases).map((base) => `${base.replace(/\/$/, '')}/admin-console`);
};

const shouldRetryWithNextBase = (error: unknown) =>
  error instanceof TypeError
  || (error instanceof ConsoleApiError
    && (error.message.includes('HTML 页面') || error.message.includes('Failed to fetch')));

const fetchJson = async <T>(path: string, options: RequestInit = {}, token?: string): Promise<T> => {
  const apiBases = buildApiBases();
  let lastError: unknown = null;

  for (let index = 0; index < apiBases.length; index += 1) {
    try {
      const response = await fetch(`${apiBases[index]}${path}`, {
        ...options,
        headers: buildHeaders(token, options.headers),
      });
      return await parseResponse<T>(response);
    } catch (error) {
      lastError = error;
      if (index === apiBases.length - 1 || !shouldRetryWithNextBase(error)) {
        throw error;
      }
    }
  }

  throw lastError instanceof Error ? lastError : new ConsoleApiError('接口请求失败，请检查后端服务。', 500);
};

const buildListQuery = (keyword = '', page = 1, pageSize = 10, language?: AdminContentLocale) => {
  const params = new URLSearchParams();
  if (keyword) params.set('q', keyword);
  if (language) params.set('lang', language);
  params.set('page', String(page));
  params.set('page_size', String(pageSize));
  const query = params.toString();
  return query ? `?${query}` : '';
};

const normalizeAdminContentItem = (item: AdminContentItem): AdminContentItem => ({
  ...item,
  cover_image_url: resolveBackendAssetUrl(item.cover_image_url) ?? null,
  qr_image_url: resolveBackendAssetUrl(item.qr_image_url) ?? null,
});
const resolveContentBasePath = (kind: AdminContentKind) => {
  if (kind === 'news') return '/news';
  if (kind === 'resource') return '/resources';
  if (kind === 'career') return '/careers';
  return '/project-cooperations';
};

export const adminConsoleApi = {
  async login(username: string, password: string) {
    return fetchJson<{ ok: true; token: string; user: AdminUser }>('/login/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password }),
    });
  },

  async me(token: string) {
    return fetchJson<{ ok: true; user: AdminUser }>('/me/', { method: 'GET' }, token);
  },

  async summary(token: string) {
    return fetchJson<{ ok: true; summary: AdminSummary }>('/summary/', { method: 'GET' }, token);
  },

  async analytics(token: string) {
    return fetchJson<{ ok: true; analytics: AdminAnalyticsPayload }>('/analytics/', { method: 'GET' }, token);
  },

  async list(kind: AdminContentKind, token: string, keyword = '', page = 1, pageSize = 10, language: AdminContentLocale = 'zh') {
    const query = buildListQuery(keyword, page, pageSize, language);
    const data = await fetchJson<AdminListResponse<AdminContentItem>>(`${resolveContentBasePath(kind)}/${query}`, { method: 'GET' }, token);
    return { ...data, results: data.results.map(normalizeAdminContentItem) };
  },

  async detail(kind: AdminContentKind, id: number, token: string) {
    const data = await fetchJson<{ ok: true; item: AdminContentItem }>(`${resolveContentBasePath(kind)}/${id}/`, { method: 'GET' }, token);
    return { ...data, item: normalizeAdminContentItem(data.item) };
  },

  async save(kind: AdminContentKind, token: string, payload: FormData, id?: number) {
    const path = id
      ? `${resolveContentBasePath(kind)}/${id}/update/`
      : `${resolveContentBasePath(kind)}/create/`;
    const data = await fetchJson<{ ok: true; item: AdminContentItem }>(path, { method: 'POST', body: payload }, token);
    return { ...data, item: normalizeAdminContentItem(data.item) };
  },

  async remove(kind: AdminContentKind, token: string, id: number) {
    return fetchJson<{ ok: true }>(`${resolveContentBasePath(kind)}/${id}/delete/`, { method: 'POST' }, token);
  },

  async listUsers(token: string, keyword = '', page = 1, pageSize = 10) {
    const query = buildListQuery(keyword, page, pageSize);
    return fetchJson<AdminListResponse<AdminUser>>(`/users/${query}`, { method: 'GET' }, token);
  },

  async createUser(token: string, payload: CreateAdminUserPayload) {
    const data = new FormData();
    Object.entries(payload).forEach(([key, value]) => data.append(key, String(value)));
    return fetchJson<{ ok: true; user: AdminUser }>('/users/create/', { method: 'POST', body: data }, token);
  },

  async updateUser(token: string, userId: number, payload: AdminUserPayload) {
    const data = new FormData();
    Object.entries(payload).forEach(([key, value]) => data.append(key, String(value)));
    return fetchJson<{ ok: true; user: AdminUser }>(`/users/${userId}/update/`, { method: 'POST', body: data }, token);
  },

  async setUserPassword(token: string, userId: number, newPassword: string) {
    const data = new FormData();
    data.append('new_password', newPassword);
    return fetchJson<{ ok: true }>(`/users/${userId}/set-password/`, { method: 'POST', body: data }, token);
  },

  async updateMyAccount(token: string, payload: UpdateOwnAccountPayload) {
    const data = new FormData();
    Object.entries(payload).forEach(([key, value]) => data.append(key, String(value)));
    return fetchJson<{ ok: true; user: AdminUser }>('/account/update/', { method: 'POST', body: data }, token);
  },

  async changeMyPassword(token: string, payload: ChangeOwnPasswordPayload) {
    const data = new FormData();
    Object.entries(payload).forEach(([key, value]) => data.append(key, String(value)));
    return fetchJson<{ ok: true }>('/account/change-password/', { method: 'POST', body: data }, token);
  },
};
